import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { AppShell } from "../components/cod/Layout";
import { Seo } from "../components/cod/Seo";
import { useAuth } from "../lib/auth";
import { employerApi, refApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Briefcase, Users, Mail, FileText, Plus, ArrowLeft, TrendingUp,
  MapPin, DollarSign, Clock, Award, CheckCircle2, XCircle, MessageCircle,
  Building2, Search, Filter,
} from "lucide-react";

const JOB_STATUS_LABELS = {
  draft: "Draft", open: "Open", on_hold: "On Hold", filled: "Filled", closed: "Closed",
};
const JOB_STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700",
  open: "bg-emerald-100 text-emerald-800",
  on_hold: "bg-amber-100 text-amber-800",
  filled: "bg-blue-100 text-blue-800",
  closed: "bg-slate-200 text-slate-600",
};
const PRIORITY_COLORS = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-slate-100 text-slate-700",
  high: "bg-amber-100 text-amber-800",
  critical: "bg-red-100 text-red-800",
};

function Stat({ icon: Icon, label, value, testid, sub }) {
  return (
    <Card data-testid={testid} className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
            <div className="text-2xl font-serif text-slate-900 mt-1">{value ?? "—"}</div>
            {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
          </div>
          {Icon && <div className="rounded-md bg-slate-100 p-2"><Icon size={18} className="text-[var(--cod-navy)]"/></div>}
        </div>
      </CardContent>
    </Card>
  );
}

function PortalHeader({ title, subtitle, right }) {
  return (
    <div className="mb-8 flex items-start justify-between gap-4">
      <div>
        <div className="text-xs uppercase tracking-widest text-[var(--cod-red)] font-semibold">Employer Portal</div>
        <h1 className="font-serif text-3xl text-slate-900 mt-1">{title}</h1>
        <div className="cod-rule-red mt-3"/>
        {subtitle && <p className="text-slate-600 mt-3">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EMPLOYER DASHBOARD — real stats + recent candidates + open orders
// ═══════════════════════════════════════════════════════════════════════════
export function EmployerDashboard() {
  const [d, setD] = useState(null);
  const [jobs, setJobs] = useState([]);
  useEffect(() => {
    employerApi.dashboard().then(setD).catch(()=>setD({}));
    employerApi.listJobs("open").then(setJobs).catch(()=>setJobs([]));
  }, []);
  return (
    <AppShell>
      <Seo title="Employer Portal · Dashboard" path="/portal/employer" noindex/>
      <PortalHeader
        title="Employer Dashboard"
        subtitle="Post hiring needs, review candidate referrals, and track placements. Your COD Business Development representative sees these actions in real time."
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="employer-stats">
        <Stat icon={Briefcase} label="Open Job Orders" value={d?.open_job_orders} testid="stat-emp-jobs" sub={`${d?.total_job_orders ?? 0} total`}/>
        <Stat icon={Users} label="Candidates in Pipeline" value={d?.candidates_in_pipeline} testid="stat-emp-candidates"/>
        <Stat icon={CheckCircle2} label="Hires (YTD)" value={d?.hires_ytd} testid="stat-emp-hires" sub={`${d?.completed_placements || 0} completed`}/>
        <Stat icon={Mail} label="Unread Messages" value={d?.unread_messages} testid="stat-emp-msgs"/>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Open Job Orders</CardTitle>
            <Link to="/portal/employer/job-orders"><Button size="sm" variant="outline" data-testid="btn-view-all-jobs">View all →</Button></Link>
          </CardHeader>
          <CardContent data-testid="emp-open-jobs" className="space-y-2">
            {jobs.length === 0 && (
              <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-6 text-center">
                No open job orders. <Link to="/portal/employer/job-orders" className="cod-link">Post one →</Link>
              </div>
            )}
            {jobs.slice(0, 6).map(j => (
              <Link to={`/portal/employer/job-orders/${j.id}`} key={j.id} data-testid={`emp-job-${j.id}`}
                className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3 hover:border-[var(--cod-red)]">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{j.role_title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{j.headcount} FTE · {j.candidate_counts?.referred || 0} candidates · {j.candidate_counts?.hired || 0} hired</div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${JOB_STATUS_COLORS[j.status]}`}>{JOB_STATUS_LABELS[j.status]}</span>
              </Link>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Recent Candidates</CardTitle></CardHeader>
          <CardContent data-testid="emp-recent-candidates" className="space-y-2">
            {(d?.recent_candidates || []).length === 0 && <div className="text-sm text-slate-500">No candidates yet.</div>}
            {(d?.recent_candidates || []).map(r => (
              <div key={r.id} className="border-b border-slate-100 pb-2 last:border-b-0">
                <div className="text-sm font-semibold text-slate-900">{r.participant?.full_name || "—"}</div>
                <div className="text-xs text-slate-500">{r.outcome || "pending"} · {r.created_at?.slice(0, 10)}</div>
              </div>
            ))}
            <Link to="/portal/employer/candidates"><Button size="sm" variant="outline" className="w-full mt-2" data-testid="btn-view-candidates">View all candidates →</Button></Link>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// JOB ORDERS — list, filter, create, edit, delete
// ═══════════════════════════════════════════════════════════════════════════
export function EmployerJobOrders() {
  const [jobs, setJobs] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const load = () => employerApi.listJobs(status !== "all" ? status : undefined)
    .then(setJobs).catch(()=>setJobs([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return jobs;
    return jobs.filter(j => (j.role_title || "").toLowerCase().includes(s)
                          || (j.description || "").toLowerCase().includes(s)
                          || (j.location || "").toLowerCase().includes(s));
  }, [jobs, q]);
  return (
    <AppShell>
      <Seo title="Employer · Job Orders" path="/portal/employer/job-orders" noindex/>
      <PortalHeader
        title="Job Orders"
        subtitle="Post structured hiring requests. COD staff use them to prioritize candidate matching and cohort planning."
        right={<Button className="cod-bg-red" onClick={()=>setShowCreate(true)} data-testid="btn-new-job-order"><Plus size={16} className="mr-1"/> New Job Order</Button>}
      />

      <div className="grid sm:grid-cols-2 gap-3 mb-4" data-testid="jobs-filters">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input placeholder="Search role, description, location…" value={q} onChange={e=>setQ(e.target.value)} className="pl-9" data-testid="jobs-search"/>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="jobs-status-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(JOB_STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3" data-testid="jobs-list">
        {filtered.length === 0 && (
          <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-8 text-center">No job orders match those filters. <button className="cod-link" onClick={()=>setShowCreate(true)}>Post one →</button></div>
        )}
        {filtered.map(j => (
          <Link to={`/portal/employer/job-orders/${j.id}`} key={j.id} data-testid={`job-row-${j.id}`}
            className="block rounded-lg border border-slate-200 bg-white p-4 hover:border-[var(--cod-red)]">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-slate-900">{j.role_title}</div>
                  {j.priority && j.priority !== "medium" && (
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${PRIORITY_COLORS[j.priority]}`}>{j.priority}</span>
                  )}
                </div>
                <div className="text-xs text-slate-600 mt-1 flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1"><Users size={12}/> {j.headcount} FTE</span>
                  {j.wage_min && <span className="flex items-center gap-1"><DollarSign size={12}/> ${j.wage_min}{j.wage_max ? `–$${j.wage_max}` : ""}/{j.wage_unit}</span>}
                  {j.location && <span className="flex items-center gap-1"><MapPin size={12}/> {j.location}</span>}
                  {j.start_date && <span className="flex items-center gap-1"><Clock size={12}/> starts {String(j.start_date).slice(0,10)}</span>}
                </div>
                {j.description && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{j.description}</p>}
                <div className="text-xs text-slate-500 mt-2">
                  {j.candidate_counts?.referred || 0} candidates · {j.candidate_counts?.hired || 0} hired · {j.filled_count || 0}/{j.headcount} filled
                </div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded flex-shrink-0 ${JOB_STATUS_COLORS[j.status]}`}>{JOB_STATUS_LABELS[j.status]}</span>
            </div>
          </Link>
        ))}
      </div>

      <JobOrderDialog open={showCreate} onOpenChange={setShowCreate} onSaved={load}/>
    </AppShell>
  );
}

function JobOrderDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(initial || {
    role_title: "", headcount: 1, wage_min: "", wage_max: "", wage_unit: "hour",
    location: "", schedule: "", start_date: "", certifications_required: [],
    experience_required: "", description: "", priority: "medium", status: "open",
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && initial) setForm(initial); }, [open, initial]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form };
      if (payload.wage_min === "") delete payload.wage_min; else payload.wage_min = parseFloat(payload.wage_min);
      if (payload.wage_max === "") delete payload.wage_max; else payload.wage_max = parseFloat(payload.wage_max);
      payload.headcount = parseInt(payload.headcount || 1, 10);
      if (!payload.start_date) delete payload.start_date;
      if (initial?.id) {
        await employerApi.patchJob(initial.id, payload);
        toast.success("Job order updated");
      } else {
        await employerApi.createJob(payload);
        toast.success("Job order posted");
      }
      onOpenChange(false); onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial ? "Edit Job Order" : "New Job Order"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Role title</Label><Input required value={form.role_title} onChange={e=>setForm({...form, role_title:e.target.value})} placeholder="e.g., Warehouse Associate" data-testid="jo-role"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Headcount</Label><Input type="number" min="1" required value={form.headcount} onChange={e=>setForm({...form, headcount:e.target.value})} data-testid="jo-headcount"/></div>
            <div><Label>Location</Label><Input value={form.location} onChange={e=>setForm({...form, location:e.target.value})} placeholder="Chicago, IL" data-testid="jo-location"/></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Wage min ($)</Label><Input type="number" step="0.01" value={form.wage_min} onChange={e=>setForm({...form, wage_min:e.target.value})} data-testid="jo-wage-min"/></div>
            <div><Label>Wage max ($)</Label><Input type="number" step="0.01" value={form.wage_max} onChange={e=>setForm({...form, wage_max:e.target.value})} data-testid="jo-wage-max"/></div>
            <div>
              <Label>Unit</Label>
              <Select value={form.wage_unit} onValueChange={v=>setForm({...form, wage_unit:v})}>
                <SelectTrigger data-testid="jo-wage-unit"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hour">/hour</SelectItem>
                  <SelectItem value="week">/week</SelectItem>
                  <SelectItem value="month">/month</SelectItem>
                  <SelectItem value="year">/year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Schedule</Label><Input value={form.schedule} onChange={e=>setForm({...form, schedule:e.target.value})} placeholder="Mon–Fri, 8am–4pm" data-testid="jo-schedule"/></div>
            <div><Label>Start date</Label><Input type="date" value={form.start_date || ""} onChange={e=>setForm({...form, start_date:e.target.value})} data-testid="jo-start-date"/></div>
          </div>
          <div>
            <Label>Certifications required (comma-separated)</Label>
            <Input value={(form.certifications_required || []).join(", ")} onChange={e=>setForm({...form, certifications_required: e.target.value.split(",").map(s=>s.trim()).filter(Boolean)})} placeholder="e.g., CNA, CPR/BLS" data-testid="jo-certs"/>
          </div>
          <div>
            <Label>Experience required</Label>
            <Input value={form.experience_required} onChange={e=>setForm({...form, experience_required:e.target.value})} placeholder="e.g., 6+ months warehouse experience" data-testid="jo-experience"/>
          </div>
          <div>
            <Label>Description</Label>
            <textarea rows={4} className="w-full rounded-md border border-slate-300 p-2 text-sm" value={form.description} onChange={e=>setForm({...form, description:e.target.value})} data-testid="jo-description"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={v=>setForm({...form, priority:v})}>
                <SelectTrigger data-testid="jo-priority"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v=>setForm({...form, status:v})}>
                <SelectTrigger data-testid="jo-status"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {Object.entries(JOB_STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="cod-bg-red" disabled={busy} data-testid="jo-submit">{busy ? "Saving…" : (initial ? "Save" : "Post Job Order")}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// JOB ORDER DETAIL — with candidate list
// ═══════════════════════════════════════════════════════════════════════════
export function EmployerJobOrderDetail() {
  const { id } = useParams();
  const [j, setJ] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const navigate = useNavigate();
  const load = () => employerApi.getJob(id).then(setJ).catch(()=>setJ(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  if (!j) return <AppShell><div className="text-sm text-slate-500">Loading…</div></AppShell>;
  const del = async () => {
    if (!window.confirm("Close this job order? It will no longer receive candidates.")) return;
    try { await employerApi.deleteJob(id); toast.success("Job order closed"); navigate("/portal/employer/job-orders"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <AppShell>
      <Seo title={`Employer · ${j.role_title}`} path={`/portal/employer/job-orders/${id}`} noindex/>
      <Link to="/portal/employer/job-orders" className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-2"><ArrowLeft size={12}/> Job Orders</Link>
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h1 className="font-serif text-3xl text-slate-900" data-testid="jo-detail-title">{j.role_title}</h1>
          <div className="cod-rule-red mt-3"/>
          <div className="text-sm text-slate-600 mt-3 flex flex-wrap gap-4">
            <span className="flex items-center gap-1"><Users size={14}/> {j.headcount} FTE ({j.filled_count || 0} filled)</span>
            {j.wage_min && <span className="flex items-center gap-1"><DollarSign size={14}/> ${j.wage_min}{j.wage_max ? `–$${j.wage_max}` : ""}/{j.wage_unit}</span>}
            {j.location && <span className="flex items-center gap-1"><MapPin size={14}/> {j.location}</span>}
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${JOB_STATUS_COLORS[j.status]}`}>{JOB_STATUS_LABELS[j.status]}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={()=>setShowEdit(true)} data-testid="btn-edit-job">Edit</Button>
          <Button variant="outline" onClick={del} data-testid="btn-close-job">Close</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Description</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="jo-detail-description">{j.description || "No description provided."}</p>
              {j.experience_required && <div className="mt-3 text-sm text-slate-700"><strong>Experience:</strong> {j.experience_required}</div>}
              {j.schedule && <div className="mt-1 text-sm text-slate-700"><strong>Schedule:</strong> {j.schedule}</div>}
              {(j.certifications_required || []).length > 0 && (
                <div className="mt-3">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Required certifications</div>
                  <div className="flex flex-wrap gap-1">
                    {j.certifications_required.map((c, i) => <span key={i} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded flex items-center gap-1"><Award size={10}/>{c}</span>)}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Candidates ({(j.candidates || []).length})</CardTitle></CardHeader>
            <CardContent className="space-y-2" data-testid="jo-candidates">
              {(j.candidates || []).length === 0 && (
                <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded p-6 text-center">No candidates referred yet. Your COD BD rep will match candidates from active cohorts as they become available.</div>
              )}
              {(j.candidates || []).map(c => (
                <CandidateRow key={c.id} c={c} onOutcome={load}/>
              ))}
            </CardContent>
          </Card>
        </div>
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div><span className="text-slate-500">Priority:</span> <span className="text-slate-900 capitalize">{j.priority}</span></div>
              <div><span className="text-slate-500">Created:</span> <span className="text-slate-900">{j.created_at?.slice(0, 10)}</span></div>
              <div><span className="text-slate-500">Start date:</span> <span className="text-slate-900">{j.start_date || "TBD"}</span></div>
              <div><span className="text-slate-500">Filled:</span> <span className="text-slate-900">{j.filled_count || 0} / {j.headcount}</span></div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Need help?</CardTitle></CardHeader>
            <CardContent className="text-sm text-slate-600 space-y-2">
              <p>Your COD BD representative gets notified whenever you post or edit a job order.</p>
              <Link to="/portal/employer/messages"><Button variant="outline" size="sm" className="w-full" data-testid="btn-msg-bd">Message your BD rep</Button></Link>
            </CardContent>
          </Card>
        </div>
      </div>
      <JobOrderDialog open={showEdit} onOpenChange={setShowEdit} initial={j} onSaved={load}/>
    </AppShell>
  );
}

function CandidateRow({ c, onOutcome }) {
  const [busy, setBusy] = useState(false);
  const setOutcome = async (outcome) => {
    setBusy(true);
    try { await refApi.outcome(c.id, { outcome, notes: "" }); toast.success(`Marked ${outcome}`); onOutcome && onOutcome(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const OUTCOME_LABEL = { hired: "Hired", accepted: "Interviewing", declined: "Declined", completed: "Placed", pending: "Pending" };
  const cur = c.outcome || "pending";
  return (
    <div className="border-b border-slate-100 pb-2 last:border-b-0" data-testid={`candidate-${c.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">{c.participant?.full_name || "—"}</div>
          <div className="text-xs text-slate-500">Referred {c.created_at?.slice(0, 10)} · {c.priority} · {OUTCOME_LABEL[cur] || cur}</div>
          {c.summary && <div className="text-xs text-slate-600 mt-1 line-clamp-2">{c.summary}</div>}
        </div>
        <div className="flex gap-1 flex-shrink-0">
          {cur === "pending" || cur === null ? (
            <>
              <Button size="sm" variant="outline" onClick={()=>setOutcome("accepted")} disabled={busy} data-testid={`cand-interview-${c.id}`}>Interview</Button>
              <Button size="sm" className="cod-bg-red" onClick={()=>setOutcome("hired")} disabled={busy} data-testid={`cand-hire-${c.id}`}><CheckCircle2 size={14} className="mr-1"/>Hire</Button>
              <Button size="sm" variant="outline" onClick={()=>setOutcome("declined")} disabled={busy} data-testid={`cand-decline-${c.id}`}>Decline</Button>
            </>
          ) : cur === "accepted" ? (
            <>
              <Button size="sm" className="cod-bg-red" onClick={()=>setOutcome("hired")} disabled={busy} data-testid={`cand-hire-${c.id}`}><CheckCircle2 size={14} className="mr-1"/>Hire</Button>
              <Button size="sm" variant="outline" onClick={()=>setOutcome("declined")} disabled={busy} data-testid={`cand-decline-${c.id}`}>Decline</Button>
            </>
          ) : (
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${cur === "hired" || cur === "completed" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{OUTCOME_LABEL[cur] || cur}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CANDIDATES — full list across all job orders
// ═══════════════════════════════════════════════════════════════════════════
export function EmployerCandidates() {
  const [rows, setRows] = useState([]);
  const [outcome, setOutcome] = useState("all");
  const [q, setQ] = useState("");
  const load = () => employerApi.candidates(outcome !== "all" ? { outcome } : {}).then(setRows).catch(()=>setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [outcome]);
  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return rows;
    return rows.filter(r => (r.participant?.full_name || "").toLowerCase().includes(s)
                          || (r.summary || "").toLowerCase().includes(s)
                          || (r.job_order?.role_title || "").toLowerCase().includes(s));
  }, [rows, q]);
  return (
    <AppShell>
      <Seo title="Employer · Candidates" path="/portal/employer/candidates" noindex/>
      <PortalHeader
        title="Candidate Referrals"
        subtitle="COD-vetted candidates matched to your open job orders. Interview, hire, or decline directly from the pipeline."
      />
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input placeholder="Search by candidate name, role, notes…" value={q} onChange={e=>setQ(e.target.value)} className="pl-9" data-testid="cand-search"/>
        </div>
        <Select value={outcome} onValueChange={setOutcome}>
          <SelectTrigger data-testid="cand-outcome-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All outcomes</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="accepted">Interviewing</SelectItem>
            <SelectItem value="hired">Hired</SelectItem>
            <SelectItem value="completed">Placed</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-3" data-testid="candidates-list">
        {filtered.length === 0 && (
          <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-8 text-center">No candidates match those filters. Post a job order and your BD rep will match candidates.</div>
        )}
        {filtered.map(c => (
          <div key={c.id} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`cand-row-${c.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-slate-900">{c.participant?.full_name || "—"}</div>
                  {c.unread_messages > 0 && (
                    <span className="text-[10px] uppercase tracking-widest bg-[var(--cod-red)] text-white px-2 py-0.5 rounded flex items-center gap-1"><MessageCircle size={10}/>{c.unread_messages} unread</span>
                  )}
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  Referred {c.created_at?.slice(0, 10)}
                  {c.job_order && <> · <Link to={`/portal/employer/job-orders/${c.job_order.id}`} className="cod-link">{c.job_order.role_title}</Link></>}
                </div>
                {c.summary && <p className="text-sm text-slate-700 mt-2 line-clamp-2">{c.summary}</p>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <CandidateRow c={c} onOutcome={load}/>
              </div>
            </div>
          </div>
        ))}
      </div>
    </AppShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HIRING ACTIVITY — chronological timeline
// ═══════════════════════════════════════════════════════════════════════════
export function EmployerHiringActivity() {
  const [events, setEvents] = useState([]);
  useEffect(() => { employerApi.hiringActivity().then(setEvents).catch(()=>setEvents([])); }, []);
  const kindLabel = { referral_received: "Candidate referred", outcome_hired: "Candidate hired",
                       outcome_accepted: "Moved to interview", outcome_declined: "Candidate declined",
                       outcome_completed: "Placement completed", outcome_pending: "Referral pending" };
  const kindColor = { referral_received: "bg-slate-100 text-slate-700",
                       outcome_hired: "bg-emerald-100 text-emerald-800",
                       outcome_completed: "bg-emerald-100 text-emerald-800",
                       outcome_accepted: "bg-blue-100 text-blue-800",
                       outcome_declined: "bg-slate-100 text-slate-600",
                       outcome_pending: "bg-amber-100 text-amber-800" };
  return (
    <AppShell>
      <Seo title="Employer · Hiring Activity" path="/portal/employer/hiring-activity" noindex/>
      <PortalHeader
        title="Hiring Activity"
        subtitle="Every candidate action in one chronological timeline — for audit, retention tracking, and grant reporting."
      />
      <div className="space-y-2" data-testid="hiring-activity-list">
        {events.length === 0 && (
          <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-8 text-center">No hiring activity yet.</div>
        )}
        {events.map((e, i) => (
          <div key={i} className="rounded-lg border border-slate-200 bg-white p-4 flex items-start gap-3" data-testid={`activity-${i}`}>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded flex-shrink-0 ${kindColor[e.kind] || "bg-slate-100 text-slate-700"}`}>{kindLabel[e.kind] || e.kind}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900">{e.participant_name}</div>
              <div className="text-xs text-slate-500">{e.date?.slice(0, 16).replace("T", " ")}</div>
              {e.summary && <div className="text-sm text-slate-700 mt-1">{e.summary}</div>}
            </div>
          </div>
        ))}
      </div>
    </AppShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ORGANIZATION PROFILE — real editor
// ═══════════════════════════════════════════════════════════════════════════
export function EmployerProfile() {
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => employerApi.getOrg().then(o => { setOrg(o); setForm({
    website: o.website || "", industry: o.industry || "", phone: o.phone || "",
    email: o.email || "", address: o.address || "", logo_url: o.logo_url || "",
    hiring_needs: o.hiring_needs || "", workforce_challenges: o.workforce_challenges || "",
    certifications_required: o.certifications_required || [],
  }); }).catch(()=>setOrg({}));
  useEffect(() => { load(); }, []);
  if (!org || !form) return <AppShell><div className="text-sm text-slate-500">Loading…</div></AppShell>;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form };
      await employerApi.patchOrg(payload);
      toast.success("Profile updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <AppShell>
      <Seo title="Employer · Organization Profile" path="/portal/employer/profile" noindex/>
      <PortalHeader
        title="Organization Profile"
        subtitle="Your profile at Chicago Opportunity Desk. Your COD Business Development representative sees these fields when matching candidates and cohorts."
      />
      <div className="mb-4 flex items-center gap-3">
        <div className="rounded-md bg-slate-100 p-3"><Building2 size={22} className="text-[var(--cod-navy)]"/></div>
        <div>
          <div className="text-lg font-serif text-slate-900" data-testid="org-name">{org.name}</div>
          <div className="text-xs text-slate-500">{(org.classifications || []).join(" · ") || "—"}</div>
        </div>
      </div>
      <form onSubmit={submit} className="max-w-2xl space-y-4" data-testid="org-profile-form">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Website</Label><Input value={form.website} onChange={e=>setForm({...form, website:e.target.value})} placeholder="https://…" data-testid="org-website"/></div>
          <div><Label>Industry</Label><Input value={form.industry} onChange={e=>setForm({...form, industry:e.target.value})} placeholder="e.g., Healthcare" data-testid="org-industry"/></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Phone</Label><Input value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} data-testid="org-phone"/></div>
          <div><Label>Contact email</Label><Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="org-email"/></div>
        </div>
        <div><Label>Address</Label><Input value={form.address} onChange={e=>setForm({...form, address:e.target.value})} data-testid="org-address"/></div>
        <div>
          <Label>Hiring needs</Label>
          <textarea rows={3} className="w-full rounded-md border border-slate-300 p-2 text-sm" value={form.hiring_needs} onChange={e=>setForm({...form, hiring_needs:e.target.value})} placeholder="e.g., 15 warehouse associates for Q2, 2nd shift" data-testid="org-hiring-needs"/>
        </div>
        <div>
          <Label>Workforce challenges</Label>
          <textarea rows={3} className="w-full rounded-md border border-slate-300 p-2 text-sm" value={form.workforce_challenges} onChange={e=>setForm({...form, workforce_challenges:e.target.value})} placeholder="e.g., High turnover, need certified CNAs" data-testid="org-workforce-challenges"/>
        </div>
        <div>
          <Label>Certifications typically required (comma-separated)</Label>
          <Input value={(form.certifications_required || []).join(", ")} onChange={e=>setForm({...form, certifications_required: e.target.value.split(",").map(s=>s.trim()).filter(Boolean)})} placeholder="e.g., CNA, Food Manager, PERC" data-testid="org-certs"/>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
          <Button type="submit" className="cod-bg-red" disabled={busy} data-testid="org-save">{busy ? "Saving…" : "Save Profile"}</Button>
        </div>
      </form>
      <div className="mt-8 text-xs text-slate-500">
        Classifications and pipeline stage are managed by COD staff to preserve the BD workflow.
        Contact your BD rep to update these attributes.
      </div>
    </AppShell>
  );
}
